import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { Subscription, catchError, of } from "rxjs";
import {
  BRAND_CAMPAIGN_STATUS_FILTERS,
  BrandCampaign,
  BrandCampaignStatus,
  BrandCampaignStatusFilter,
} from "../../../shared/models/brandCampaign";
import { SortByDateMode } from "../../../shared/enums/campaign.enums";
import { BrandService } from "../../../shared/services/api/brand.service";
import { DialogService } from "../../../shared/services/dialog.service";
import { NotificationService } from "../../../shared/services/notification.service";
import { BrandCampaignDialogService } from "../brand-campaign-dialog.service";
import { BrandCampaignService } from "../shared/services/brand-campaign.service";
import { BrandUrl } from "../../brand.url";
import { valueAscOrder } from "../../../shared/utils/value-asc-order";
import { assertHtmlInput } from "../../../shared/utils/assert";

@Component({
  selector: "app-brand-campaign-list",
  templateUrl: "./brand-campaign-list.component.html",
  styleUrl: "./brand-campaign-list.component.scss",
})
export class BrandCampaignListComponent implements OnInit, OnDestroy {
  protected readonly SORT_MODES = SortByDateMode;
  protected readonly STATUS_FILTERS = BRAND_CAMPAIGN_STATUS_FILTERS;
  protected readonly valueAscOrder = valueAscOrder;
  protected brandName = "";
  protected campaigns: BrandCampaign[] = [];
  protected deletingCampaignId?: number;
  protected loading = true;
  protected loadingError = false;
  protected showFilter = false;
  protected shownCampaigns: BrandCampaign[] = [];
  protected sortMode = SortByDateMode.Desc;

  private readonly subs = new Subscription();
  private nameFilter = "";
  private statusFilter: BrandCampaignStatusFilter = {};

  constructor(
    private readonly brandCampaignDialogService: BrandCampaignDialogService,
    private readonly brandCampaignService: BrandCampaignService,
    private readonly brandService: BrandService,
    private readonly dialogService: DialogService,
    private readonly notificationService: NotificationService,
    private readonly router: Router,
  ) {}

  public ngOnInit(): void {
    this.onViewAllCampaigns();
    this.getCampaigns();
    this.brandName = this.brandService.currentBrand.companyName;
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  protected getCampaigns(): void {
    this.brandCampaignService
      .getBrandCampaigns(BrandCampaignStatus.All)
      .pipe(
        catchError(() => {
          this.loading = false;
          this.loadingError = true;
          return of();
        }),
      )
      .subscribe((campaigns: BrandCampaign[]) => {
        this.loading = false;
        this.campaigns = campaigns;
        this.applyFilters();
        this.shownCampaigns = this.sortByDate(this.sortMode);
        this.loadingError = false;
      });
  }

  protected async newCampaign(): Promise<void> {
    try {
      const campaign = await this.brandCampaignDialogService.showCreation();

      if (campaign) {
        this.router.navigate([
          BrandUrl.Campaign.DetailsSettings(
            this.brandService.currentBrandId,
            campaign.id,
          ),
        ]);
      }
    } catch (err) {
      this.notificationService.error("shared.changesCouldNotBeSaved");
    }
  }

  protected deleteCampaignConfirm(campaign: BrandCampaign): void {
    this.dialogService.confirm({
      title: "brand.campaignList.deleteConfirmTitle",
      message: "brand.campaignList.deleteConfirm",
      onConfirm: () => this.onDeleteCampaign(campaign),
    });
  }

  private onDeleteCampaign(campaign: BrandCampaign): void {
    this.deletingCampaignId = campaign.id;
    this.brandCampaignService.deleteCampaign(campaign.id).subscribe({
      next: () => {
        this.deletingCampaignId = undefined;
        this.campaigns = this.campaigns.filter((c) => c.id !== campaign.id);
        this.shownCampaigns = this.shownCampaigns.filter(
          (c) => c.id !== campaign.id,
        );
        this.notificationService.success(
          "brand.campaignList.campaignDeletedSuccessfully",
        );
      },
      error: () => {
        this.deletingCampaignId = undefined;
        this.notificationService.error("shared.errorSavingChanges");
      },
    });
  }

  protected duplicateCampaignConfirm(campaign: BrandCampaign): void {
    this.dialogService.confirm({
      title: "brand.campaignList.duplicateConfirmTitle",
      message: "brand.campaignList.duplicateConfirm",
      onConfirm: () => this.onDuplicateCampaign(campaign),
    });
  }

  private onDuplicateCampaign(campaign: BrandCampaign): void {
    this.brandCampaignService.duplicateCampaign(campaign.id).subscribe({
      next: (newCampaign) => {
        this.campaigns.push(newCampaign);
        this.shownCampaigns = this.campaigns;
        this.applyFilters();
        this.shownCampaigns = this.sortByDate(this.sortMode);
        this.notificationService.success(
          "brand.campaignList.campaignDuplicatedSuccessfully",
        );
      },
      error: () => {
        this.notificationService.error("shared.errorSavingChanges");
      },
    });
  }

  protected onFilterCampaigns(filter: BrandCampaignStatus): void {
    this.statusFilter[filter] = !this.statusFilter[filter];
    this.applyFilters();
    this.shownCampaigns = this.sortByDate(this.sortMode);
  }

  protected onViewAllCampaigns(): void {
    Object.values(BrandCampaignStatus).forEach(
      (status) => (this.statusFilter[status] = false),
    );
    this.shownCampaigns = this.campaigns;
  }

  protected onSearchChange(event: Event): void {
    assertHtmlInput(event.target);

    this.nameFilter = event.target.value;
    this.applyFilters();
  }

  protected onChangeOrder(): void {
    this.sortMode =
      this.sortMode === SortByDateMode.Asc
        ? SortByDateMode.Desc
        : SortByDateMode.Asc;
    this.shownCampaigns = this.sortByDate(this.sortMode);
  }

  protected filterSelected(): boolean {
    if (!this.statusFilter) {
      return false;
    }
    for (const key of this.getStatusFilterKeys()) {
      if (this.statusFilter[key]) {
        return true;
      }
    }
    return false;
  }

  private getStatusFilterKeys(): BrandCampaignStatus[] {
    return Object.values(BrandCampaignStatus);
  }

  private applyFilters(): void {
    let filteredCampaigns: BrandCampaign[] = [];

    filteredCampaigns = filteredCampaigns.concat(
      this.campaigns.filter((c) => {
        let activeFilter = false;
        for (const filter of this.getStatusFilterKeys()) {
          if (this.statusFilter[filter]) {
            activeFilter = true;
            if (c.campaignBrandStatus === filter) {
              return true;
            }
          }
        }

        return !activeFilter;
      }),
    );

    this.shownCampaigns = this.nameFilter
      ? filteredCampaigns.filter((c) =>
          c.name.toLowerCase().includes(this.nameFilter.toLowerCase()),
        )
      : filteredCampaigns;
  }

  protected isFilterActive(status: BrandCampaignStatus): boolean {
    return this.statusFilter[status] ?? false;
  }

  private sortByDate(type: SortByDateMode) {
    if (type === SortByDateMode.Asc) {
      return this.shownCampaigns.sort((c1, c2) =>
        c1.startDate > c2.startDate ? 1 : -1,
      );
    }

    return this.shownCampaigns.sort((c1, c2) =>
      c1.startDate < c2.startDate ? 1 : -1,
    );
  }

  protected async showMoveCampaignDialog(
    campaign: BrandCampaign,
  ): Promise<void> {
    const moved = await this.brandCampaignDialogService.showMove(campaign);

    if (moved) {
      this.loading = true;
      this.notificationService.success(
        "brand.campaignList.campaignMovedSuccessfully",
      );
      this.getCampaigns();
    }
  }
}
